import { cn, RiCompass4Line, Stack } from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
} from '@/components/NCExplainers/Explainer';
import { NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
// eslint-disable-next-line import/no-cycle
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PlotIndicatorCard } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotIndicatorCard';
import { isNullish } from '@/utils/booleans';
import { getHexFromString } from '@/utils/getHexFromString';

type ChartDataPoint = Record<string, number | string> & {
  yearLabel: string;
};

const getGcd = (a: number, b: number): number => {
  let x = Math.abs(Math.max(a, b));
  let y = Math.abs(Math.min(a, b));

  while (y >= 1) {
    const remainder = x % y;
    x = y;
    y = remainder;
  }
  return x;
};
const getGCD = (numbers: number[]): number => {
  if (numbers.length === 0) return 0;
  return Math.round(numbers.reduce((acc, curr) => getGcd(acc, curr)));
};

const getRatio = (a: number, b: number) => {
  if (a === 0 || b === 0) {
    return { a, b };
  }

  const gcd = Math.abs(getGCD([a, b]));

  const aRatio = Math.round(a / gcd);
  const bRatio = Math.round(b / gcd);

  return { aRatio, bRatio };
};

const formatRatio = (a: number, b: number) => {
  const { aRatio, bRatio } = getRatio(a, b);
  const aRatioFormatted = getDisplayNumber(aRatio ?? 0, window.navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const bRatioFormatted = getDisplayNumber(bRatio ?? 0, window.navigator.language, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${aRatioFormatted}:${bRatioFormatted}`;
};

type FloraFaunaCompositionProps = {
  data: PlotReportFactElement[];
  originLabel?: string;
  kingdomLabel?: string;
  groupLabel?: string;
  nativeValue?: string;
  invasiveValue?: string;
  categoryShades?: Record<string, string>;
};

export const FloraFaunaComposition = (props: FloraFaunaCompositionProps) => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.floraFauna.composition.labels.heading')}>
      <FloraFaunaCompositionCard {...props} />
    </PlotCardWrapper>
  );
};

const FloraFaunaCompositionCard = ({
  data,
  originLabel = 'origin',
  kingdomLabel = 'kingdom',
  groupLabel = 'group',
  nativeValue = 'native',
  invasiveValue = 'invasive',
  categoryShades = { flora: '#5dbd26', fauna: '#c0880e' },
}: FloraFaunaCompositionProps) => {
  const { t } = useTranslation();

  const chartData = getChartData(data, originLabel, kingdomLabel, groupLabel, nativeValue, invasiveValue);
  const latestFact = chartData.length > 0 ? (chartData[chartData.length - 1] as ChartDataPoint) : undefined;

  if (isNullish(data)) {
    return null;
  }

  const cardProps = {
    'data-testid': 'index-card-nca',
    explainer: <FloraFaunaCompositionExplainer />,
  };

  const TITLE = t(`shared.ncaDetail.details.floraFauna.composition.labels.heading`);

  const headerProps = {
    title: TITLE,
    className: 'lg:grid-cols-[1fr_1fr]',
  };
  const footerProps = {
    logos: ['tnfd', 'gri', 'sbtn', 'csrd', 'sdg', 'npi', 'eudr', 'sai'],
  };

  const statProps = {
    children: !latestFact ? null : (
      <LatestFacts latestFact={latestFact} nativeLabel={nativeValue} invasiveLabel={invasiveValue} />
    ),
  };

  const graphHeaderProps = {
    heading: TITLE,
    popover: {
      title: t('shared.ncaDetail.details.floraFauna.composition.explainer.floraFaunaComposition.title'),
      body: t('shared.ncaDetail.details.floraFauna.composition.explainer.floraFaunaComposition.body'),
    },
    className: 'w-full px-6 sm:px-0',
  };

  const graphProps = {
    children: !chartData?.length ? null : (
      <GraphTile chartData={chartData as ChartDataPoint[]} categoryShades={categoryShades} />
    ),
    graphHeaderProps,
  };

  return (
    <PlotIndicatorCard
      cardProps={cardProps}
      headerProps={headerProps}
      footerProps={footerProps}
      statProps={statProps}
      graphProps={graphProps}
    />
  );
};

const LatestFacts = ({
  latestFact,
  nativeLabel,
  invasiveLabel,
}: {
  latestFact: ChartDataPoint;
  nativeLabel: string;
  invasiveLabel: string;
}) => {
  const { t } = useTranslation();
  const floraFacts = Object.fromEntries(
    Object.entries(latestFact)
      .filter(([key]) => key.includes('flora') && key !== 'yearLabel')
      .map(([key, value]) => [key, value as number]),
  );
  const faunaFacts = Object.fromEntries(
    Object.entries(latestFact)
      .filter(([key]) => !key.includes('flora') && key !== 'yearLabel')
      .map(([key, value]) => [key, value as number]),
  );

  const totalFlora = Object.values(floraFacts).reduce<number>((acc, value) => acc + (value as number), 0);
  const floraNativeKey = Object.keys(floraFacts).find((key) => key.includes(nativeLabel));
  const floraInvasiveKey = Object.keys(floraFacts).find((key) => key.includes(invasiveLabel));
  const floraNativeValue = floraNativeKey ? (floraFacts[`${floraNativeKey}`] as number) : 0;
  const floraInvasiveValue = floraInvasiveKey ? (floraFacts[`${floraInvasiveKey}`] as number) : 0;
  const floraNativePercentage = totalFlora > 0 ? (floraNativeValue / totalFlora) * 100 : 0;
  const floraInvasivePercentage = totalFlora > 0 ? (floraInvasiveValue / totalFlora) * 100 : 0;
  const floraProportionDisplay = `${t(
    'shared.ncaDetail.details.floraFauna.composition.labels.nativeToInvasiveProportion',
    {
      nativePct: floraNativePercentage.toFixed(0),
      invasivePct: floraInvasivePercentage.toFixed(0),
    },
  )} (${formatRatio(floraNativeValue, floraInvasiveValue)})`;

  const totalFauna = Object.values(faunaFacts).reduce<number>((acc, value) => acc + (value as number), 0);
  const faunaNativeValue = Object.entries(faunaFacts).reduce((acc, [key, value]) => {
    if (key.includes(nativeLabel)) {
      return acc + (value as number);
    }
    return acc;
  }, 0);
  const faunaInvasiveValue = Object.entries(faunaFacts).reduce((acc, [key, value]) => {
    if (key.includes(invasiveLabel)) {
      return acc + (value as number);
    }
    return acc;
  }, 0);

  const faunaNativePercentage = totalFauna > 0 ? (faunaNativeValue / totalFauna) * 100 : 0;
  const faunaInvasivePercentage = totalFauna > 0 ? (faunaInvasiveValue / totalFauna) * 100 : 0;
  const faunaProportionDisplay = `${t(
    'shared.ncaDetail.details.floraFauna.composition.labels.nativeToInvasiveProportion',
    {
      nativePct: faunaNativePercentage.toFixed(0),
      invasivePct: faunaInvasivePercentage.toFixed(0),
    },
  )} (${formatRatio(faunaNativeValue, faunaInvasiveValue)})`;

  const latestFactDate = latestFact?.yearLabel ?? '';

  return (
    <Stack className='flex flex-col justify-start items-start gap-10'>
      <NCFactCapsule
        label={t('shared.ncaDetail.details.floraFauna.composition.labels.proportionOfFlora')}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
          body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: latestFactDate }),
          popoverTriggerProps: {
            className: 'text-xs',
          },
        }}
      >
        <span>{floraProportionDisplay}</span>
      </NCFactCapsule>
      <NCFactCapsule
        label={t('shared.ncaDetail.details.floraFauna.composition.labels.proportionOfFauna')}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
          body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: latestFactDate }),
          popoverTriggerProps: {
            className: 'text-xs',
          },
        }}
      >
        <span>{faunaProportionDisplay}</span>
      </NCFactCapsule>
    </Stack>
  );
};

const getChartData = (
  data: PlotReportFactElement[],
  originLabel: string,
  kingdomLabel: string,
  groupLabel: string,
  nativeLabel: string,
  invasiveLabel: string,
): ChartDataPoint[] => {
  const nativeInvasiveGroup = data.filter((entry) => {
    const origin = entry.labels?.find((label) => label.name === originLabel)?.value as string | undefined;
    return origin === nativeLabel || origin === invasiveLabel;
  });
  const sortedDates: string[] = [
    ...new Set(nativeInvasiveGroup.filter((entry) => !isNullish(entry.date)).map((entry) => entry.date as string)),
  ].sort();

  return sortedDates.map((date) => {
    const dateData = nativeInvasiveGroup.filter((entry) => entry.date === date);

    const categoriesData = dateData.reduce(
      (acc, cur) => {
        const kingdom = cur.labels?.find((label) => label.name === kingdomLabel)?.value as string | undefined;
        const group = cur.labels?.find((label) => label.name === groupLabel)?.value as string | undefined;

        const category = group ?? kingdom;

        const origin = cur.labels?.find((label) => label.name === originLabel)?.value as string | undefined;
        if (!category || !origin) return acc;

        acc[`${category}_${origin}`] = (acc[`${category}_${origin}`] || 0) + (cur.value as number);
        return acc;
      },
      {} as Record<string, number>,
    );

    const year = new Date(date).getFullYear().toString();

    return {
      yearLabel: year,
      ...categoriesData,
    };
  });
};

const GraphTile = ({
  chartData,
  categoryShades,
}: {
  chartData: ChartDataPoint[];
  categoryShades?: Record<string, string>;
}) => {
  const { t } = useTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const hasSelectedCategories = selectedCategories.length > 0;

  const categories = Object.keys(chartData?.[0] ?? {}).filter((key) => key !== 'yearLabel');
  const BAR_CATEGORIES = Object.values(categories).map((category) => ({
    dataKey: category,
    key: category,
    id: category,
    fill: categoryShades?.[`${category}`] ?? getHexFromString(category),
    color: categoryShades?.[`${category}`] ?? getHexFromString(category),
    barColors: [categoryShades?.[`${category}`] ?? getHexFromString(category)],
    label: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
    shape: ChartShape.bar,
    stackId: category.split('_')[0], // stack by group
    barSize: 20,
  }));

  const filteredBars = hasSelectedCategories
    ? BAR_CATEGORIES.filter((item) => selectedCategories.includes(item.dataKey))
    : BAR_CATEGORIES;

  const handleLegendClick = (category: string) => {
    let newSelectedCategories = selectedCategories.includes(category)
      ? [...selectedCategories.filter((c) => c !== category)]
      : [...selectedCategories, category];

    if (newSelectedCategories.length === categories.length) {
      newSelectedCategories = [];
    }

    setSelectedCategories(newSelectedCategories);
  };

  const legendItems: ChartLegendBarItem[] = [
    {
      label: t('global.misc.all'),
      color: '',
      shape: 'line',
      onClick: () => setSelectedCategories([]),
      className: cn({
        'bg-transparent rounded-3xl border border-text-secondary px-2 py-1': true,
        'bg-neutral-hover': selectedCategories.length === 0,
      }),
    },
    ...categories.map((category) => ({
      label: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
      color: categoryShades?.[`${category}`] ?? getHexFromString(category),
      onClick: () => handleLegendClick(category),
      shape: 'column',
      className: cn({
        'bg-transparent rounded-3xl border border-text-secondary px-2 py-1': true,
        'bg-neutral-hover': selectedCategories.includes(category),
      }),
    })),
  ];

  const widgetConfiguration = {
    name: 'flora_fauna_composition_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'flora-fauna-composition-graph-chart',
      data: chartData,
      chartConfig: filteredBars,
      legendItems,
      xAxisProps: {
        dataKey: 'yearLabel',
        type: 'category',
      },
      yAxisProps: {
        domain: minMaxAxisDomain(0.98, 1.05),
      },
    },
  };

  return <WidgetSlotRenderer nestedWidget={widgetConfiguration} />;
};

const FloraFaunaCompositionExplainer = () => {
  const { t } = useTranslation();

  return (
    <Explainer>
      <ExplainerTrigger data-testid='flora-fauna-composition-explainer'>
        {t('global.howDoWeMeasureThis')}
      </ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>
          {t(`shared.ncaDetail.details.floraFauna.composition.explainer.howDoWeMeasureThis.title`)}
        </ExplainerTitle>
        <Stack className='items-start pb-6'>
          <ExplainerBody>
            <ExplainerCopy
              body={t(`shared.ncaDetail.details.floraFauna.composition.explainer.howDoWeMeasureThis.body`)}
            />
          </ExplainerBody>
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
